import { useEffect, useRef } from 'react';
import useSessionStorage, { SessionStorageReservedKey } from '../global/useSessionStorage';
import useBasket from '../basket/useBasket';
import useOrderFlow from './useOrderFlow';
import useAppMethods from '../utility/useAppMethods';
import useAppState from '../global/useAppState';
import useStore from '../store/useStore';
import { REDUX_STORE_SLICES } from '../../services/exports/Constants';

interface Options {
  watchBasketReopen?: boolean;
  // While true, a detected 0 → ≥1 basket transition is held back instead of opening the
  // order-method modal immediately. Consumers pass "another modal is on screen" here —
  // the mobile ModalRouter holds a single modal at a time, so opening right away would
  // replace (and instantly unmount) the menu-item modal mid add-to-cart flow.
  holdReopen?: boolean;
}

const DISMISSED_VALUE = 'dismissed';

export default function useOrderMethodModalGate(options: Options = {}) {
  const { watchBasketReopen = false, holdReopen = false } = options;

  const { getItem, setItem, removeItem } = useSessionStorage();
  const { basketItemsCount } = useBasket();
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const { toggleOrderMethodModal } = useAppMethods();
  const { orderMethodModalProps } = useAppState();
  const { franchise } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);

  const wasDismissed = getItem(SessionStorageReservedKey.OrderMethodModalDismissed) === DISMISSED_VALUE;

  const markDismissed = (): void => {
    setItem(SessionStorageReservedKey.OrderMethodModalDismissed, DISMISSED_VALUE);
  };

  const clearDismissed = (): void => {
    removeItem(SessionStorageReservedKey.OrderMethodModalDismissed);
  };

  // Tracks the previous basket count so we can detect the 0 → ≥1 transition. Initialised to
  // the current count so a hook mounted with a non-empty basket does not re-open the modal.
  const previousCountRef = useRef(basketItemsCount);
  const hasReopenedRef = useRef(false);
  const pendingReopenRef = useRef(false);

  useEffect(() => {
    if (!watchBasketReopen) {
      return;
    }

    const previous = previousCountRef.current;
    previousCountRef.current = basketItemsCount;

    if (hasReopenedRef.current || pendingReopenRef.current) {
      return;
    }

    // The reopen-on-first-add flow exists so multi-location guests who dismissed the
    // auto-open still pick a location before checkout. Single-location shops already
    // have their company resolved — reopening there only interrupts the add-to-cart.
    if (!franchise?.has_multiple_companies) {
      return;
    }

    if (isOrderMethodInfoComplete) {
      return;
    }

    if (orderMethodModalProps?.openModal) {
      return;
    }

    if (previous !== 0 || basketItemsCount < 1) {
      return;
    }

    pendingReopenRef.current = true;
  }, [
    watchBasketReopen,
    basketItemsCount,
    isOrderMethodInfoComplete,
    orderMethodModalProps?.openModal,
    franchise?.has_multiple_companies,
  ]);

  useEffect(() => {
    if (!watchBasketReopen || !pendingReopenRef.current || hasReopenedRef.current) {
      return;
    }

    if (holdReopen || orderMethodModalProps?.openModal) {
      return;
    }

    if (isOrderMethodInfoComplete) {
      pendingReopenRef.current = false;

      return;
    }

    pendingReopenRef.current = false;
    hasReopenedRef.current = true;
    toggleOrderMethodModal({ openModal: true });
  }, [watchBasketReopen, holdReopen, isOrderMethodInfoComplete, orderMethodModalProps?.openModal, basketItemsCount]);

  return {
    wasDismissed,
    markDismissed,
    clearDismissed,
  };
}
